
import React from "react";
import { CheckCircle } from "lucide-react";
import { Card, CardHeader, CardContent, CardDescription, CardTitle, CardFooter } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { AIStaffRole } from "@/lib/types";

interface InterviewStageProps {
  candidate: {
    id: number;
    name: string;
    avatar: string;
    role: AIStaffRole;
    description: string;
    skills: string[];
    benefits: string;
  } | undefined;
  roleCapabilities: Record<AIStaffRole, string[]>;
}

const InterviewStage: React.FC<InterviewStageProps> = ({ candidate, roleCapabilities }) => {
  if (!candidate) return null;
  
  const capabilities = roleCapabilities[candidate.role] || [];
  
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-4">
        <h3 className="text-2xl font-bold">What {candidate.name} Can Do For You</h3>
        <p className="text-muted-foreground">
          Explore the capabilities of your selected AI assistant and how they can help your business grow.
        </p>
        
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="text-4xl">{candidate.avatar}</div>
              <div>
                <CardTitle>{candidate.name}</CardTitle>
                <CardDescription>AI-powered business assistant</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h4 className="font-medium mb-3">Key Capabilities</h4>
              <ul className="space-y-2">
                {capabilities.map((capability, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                    <span className="text-sm">{capability}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="pt-2 border-t">
              <h4 className="font-medium mb-2">Performance Rating</h4>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Efficiency</span>
                    <span>92%</span>
                  </div>
                  <Progress value={92} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Accuracy</span>
                    <span>88%</span>
                  </div>
                  <Progress value={88} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Response Time</span>
                    <span>95%</span>
                  </div>
                  <Progress value={95} className="h-2" />
                </div>
              </div>
            </div>
            
            <div className="pt-2 border-t">
              <h4 className="font-medium mb-2">Business Impact</h4>
              <div className="bg-muted p-4 rounded-md">
                <p className="text-sm">
                  Businesses using {candidate.name} report an average of:
                </p>
                <ul className="mt-2 space-y-1">
                  <li className="text-sm flex items-center gap-2">
                    <CheckCircle className="h-3 w-3 text-green-500" />
                    <span>30% increase in productivity</span>
                  </li>
                  <li className="text-sm flex items-center gap-2">
                    <CheckCircle className="h-3 w-3 text-green-500" />
                    <span>25% reduction in operational costs</span>
                  </li>
                  <li className="text-sm flex items-center gap-2">
                    <CheckCircle className="h-3 w-3 text-green-500" />
                    <span>20+ hours saved per week on routine tasks</span>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InterviewStage;
